import { makeAutoObservable, runInAction } from "mobx";
import { apiService } from "../../services/api.service";
import { QUESTIONNAIRE_VERSION, mapOnboardingToShortQuestionnaireAnswers } from "../../constants/questionnaire";
import type {
  OnboardingData,
  QuestionnaireProgressState,
  QuestionnaireStoreState,
  QuestionnaireSubmissionResult,
} from "../../types";

export class QuestionnaireStore implements QuestionnaireStoreState {
  isSubmitting = false;
  error: string | null = null;
  lastSubmission: QuestionnaireSubmissionResult | null = null;
  progress: QuestionnaireProgressState | null = null;

  private submitPromise: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  private setError(error: string | null): void {
    this.error = error;
  }

  private setProgress(progress: QuestionnaireProgressState | null): void {
    this.progress = progress;
  }

  private async ensureProgress(): Promise<QuestionnaireProgressState> {
    if (this.progress) {
      return this.progress;
    }

    const stateResponse = await apiService.getQuestionnaireState();
    if (stateResponse.success && stateResponse.data?.attempt_in_progress) {
      const { attempt_id, questionnaire_id, version_answered } = stateResponse.data;
      if (attempt_id && questionnaire_id) {
        const resumed: QuestionnaireProgressState = {
          attemptId: attempt_id,
          questionnaireId: questionnaire_id,
          version: version_answered ?? QUESTIONNAIRE_VERSION,
        };
        runInAction(() => {
          this.setProgress(resumed);
        });
        return resumed;
      }
    }

    const startResponse = await apiService.startQuestionnaire(QUESTIONNAIRE_VERSION);
    if (!startResponse.success || !startResponse.data) {
      throw new Error(startResponse.error || "Nao foi possivel iniciar o questionario");
    }

    const started: QuestionnaireProgressState = {
      attemptId: startResponse.data.attempt_id,
      questionnaireId: startResponse.data.questionnaire_id,
      version: startResponse.data.version,
    };
    runInAction(() => {
      this.setProgress(started);
    });

    return started;
  }

  async submitFromOnboarding(data: OnboardingData): Promise<void> {
    if (this.submitPromise) {
      return this.submitPromise;
    }

    this.submitPromise = this.performSubmitFromOnboarding(data).finally(() => {
      runInAction(() => {
        this.submitPromise = null;
      });
    });

    return this.submitPromise;
  }

  private async performSubmitFromOnboarding(data: OnboardingData): Promise<void> {
    runInAction(() => {
      this.isSubmitting = true;
      this.setError(null);
    });

    try {
      const progress = await this.ensureProgress();
      const answers = mapOnboardingToShortQuestionnaireAnswers(data);

      for (const answer of answers) {
        const saveResponse = await apiService.saveQuestionnaireAnswer(progress.attemptId, answer);
        if (!saveResponse.success) {
          throw new Error(saveResponse.error || "Falha ao salvar resposta");
        }
      }

      const submitResponse = await apiService.submitQuestionnaire(progress.questionnaireId, answers);
      if (!submitResponse.success || !submitResponse.data) {
        throw new Error(submitResponse.error || "Falha ao submeter questionario");
      }

      runInAction(() => {
        this.lastSubmission = {
          message: submitResponse.data!.message,
          profileGenerated: submitResponse.data!.profile_generated,
          profileCode: submitResponse.data!.profile_code,
        };
        this.setProgress(null);
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro inesperado ao integrar o questionario";
      runInAction(() => {
        this.setError(errorMessage);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }

  clearError(): void {
    this.setError(null);
  }
}
